import { userRole, userRoleFormate } from "@/constant";
import { globalModalProps } from "@/constant/globalConstant";
import { useQueryParams } from "@/hooks/useQueryParams";
import { useGetAllSupportQuery, useUpdateSupportMutation } from "@/redux/api/supportApi";
import { useGetAllSupportResponseQuery } from "@/redux/api/supportResponseApi";
import { App, Avatar, Button, Collapse, Empty, Image, Spin, Tag } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { FiUser } from "react-icons/fi";
import { RiSpam2Line } from "react-icons/ri";
import { useSelector } from "react-redux";
import ResponseForm from "./ResponseForm";
import { Link } from "react-router-dom";

dayjs.extend(relativeTime);

const SupportInboxMessages = () => {
  const { queryParams } = useQueryParams();
  const user = useSelector((state) => state.user?.userDetails) || {};
  const { modal, notification } = App.useApp();

  // states
  const [headerActiveKey, setHeaderActiveKey] = useState([]);
  const [bodyActiveKey, setBodyActiveKey] = useState([]);

  // rtk query
  const { isFetching: supportLoading, data: supportData } = useGetAllSupportQuery(
    {
      _id: queryParams?.inbox,
      populate: "user resolvedBy user.business",
    },
    { refetchOnFocus: true, refetchOnMountOrArgChange: true, refetchOnReconnect: true },
  );

  const support = supportData?.data?.[0];

  const [updateSupport, { isLoading: supportUpdating }] = useUpdateSupportMutation();
  const { isFetching, data: supportResponse } = useGetAllSupportResponseQuery({
    support: support?._id,
    populate: "sender",
    sortOrder: "asc",
  });

  // effect
  useEffect(() => {
    if (supportResponse?.data?.length) {
      setBodyActiveKey([supportResponse?.data?.length - 1]);
      setHeaderActiveKey([]);
    } else {
      setHeaderActiveKey(["header-1"]);
      setBodyActiveKey([]);
    }
  }, [supportResponse?.data]);

  // handler
  const resolvedHandler = async (id, status, message) => {
    await modal.confirm({
      ...globalModalProps,
      content: `Do you wish to mark this ticket as ${message || status}?`,
      onOk: async () => {
        try {
          const res = await updateSupport({ id, body: { status, resolvedDate: new Date(), resolvedBy: user?._id } }).unwrap();
          if (res?.success) {
            notification.success({
              message: "Support ticket resolved successfully.",
              duration: 2,
              showProgress: true,
            });
          }
        } catch (error) {
          notification.error({
            message: error?.data?.message || "Support ticket resolution failed.",
            duration: 2,
            showProgress: true,
          });
        }
      },
    });
  };

  // const
  const loading = isFetching || supportLoading || supportUpdating;
  const userBusiness = Array.isArray(support?.user?.business) ? support?.user?.business[0] : support?.user?.business;

  return (
    <Spin spinning={loading} wrapperClassName="w-100">
      <div className="d-flex flex-column w-100 h-100">
        {queryParams?.inbox && support && Object.keys(support)?.length > 0 ? (
          <>
            <div>
              <div className="d-flex">
                <div>
                  <div>Resolved Date : {support?.resolvedDate && dayjs(support?.resolvedDate).format("DD/MM/YYYY | hh:mm A")} </div>
                  <div>
                    Resolved By :
                    {support?.resolvedBy && typeof support?.resolvedBy === "object"
                      ? ` ${support?.resolvedBy?.name} ${support?.resolvedBy?.role !== userRole.superAdmin ? `(${support?.resolvedBy?.loginId})` : ""}  | ${userRoleFormate[support?.resolvedBy?.role]}`
                      : ""}
                  </div>
                </div>

                {[userRole.customer].includes(user?.role) && (
                  <div className="ms-auto align-items-center d-flex gap-2">
                    <Button
                      variant="filled"
                      onClick={() => resolvedHandler(support?._id, "completed")}
                      shape="circle"
                      icon={<FaCheck />}
                      type="primary"
                      className="ms-auto"
                      style={{ backgroundColor: "#22c55e", borderColor: "#22c55e" }}
                      disabled={support?.status === "completed"}
                      title={support?.status === "completed" ? "Resolved" : "Mark As Completed"}
                    />
                  </div>
                )}

                {[userRole.superAdmin, userRole.businessAdmin].includes(user?.role) ? (
                  <div className="ms-auto align-items-center d-flex gap-2">
                    {support?.status ? (
                      <Tag color={support?.status === "spam" ? "red" : "success"} className="fw-semibold p-3">
                        Resolved
                      </Tag>
                    ) : (
                      <>
                        <Button
                          variant="filled"
                          onClick={() => resolvedHandler(support?._id, "completed", "read")}
                          shape="circle"
                          icon={<FaCheck />}
                          type="primary"
                          className=""
                          style={{ backgroundColor: "#22c55e", borderColor: "#22c55e" }}
                          disabled={support?.status === "completed"}
                          title={support?.status === "completed" ? "Resolved" : "Mark As Read"}
                        />

                        <Button
                          variant="filled"
                          shape="circle"
                          icon={<RiSpam2Line />}
                          type="primary"
                          danger
                          onClick={() => resolvedHandler(support?._id, "spam")}
                          disabled={support?.status === "spam"}
                          title={support?.status === "spam" ? "Resolved" : "Mark As Spam"}
                        />
                      </>
                    )}

                    {user?.role === userRole.superAdmin && (
                      <Link to={`/users/details/${support?.user?._id}`} target="_blank">
                        <Button
                          style={{ backgroundColor: "#6b7280", borderColor: "#6b7280" }}
                          shape="circle"
                          icon={<FiUser />}
                          type="primary"
                          title="View User Profile"
                        />
                      </Link>
                    )}
                  </div>
                ) : support?.status ? (
                  <div className="ms-auto">
                    <Tag color={support?.status === "spam" ? "red" : "success"} className="fw-semibold fs-5 p-3">
                      Resolved
                    </Tag>
                  </div>
                ) : (
                  ""
                )}
              </div>
              <hr className="border-secondary-subtle my-2" />

              <div>
                <h3 className="fw-bold fs-5">
                  <span className="text-capitalize"> {support?.type} </span>: {support?.title}
                </h3>
                <div>Request Date : {dayjs(support?.createdAt).format("DD/MM/YYYY hh:mm A")}</div>
                {typeof support?.user === "object" && (
                  <div>
                    Sent By : <span className="text-capitalize"> {userRoleFormate[support?.user?.role]} </span>
                    {userBusiness?.name ? `[${userBusiness?.name} (${userBusiness?.id})]` : ""}
                  </div>
                )}
              </div>
              <hr className="border-secondary-subtle my-2" />
              <div>
                <Collapse
                  ghost={true}
                  onChange={() =>
                    setHeaderActiveKey((prev) => (prev.includes("header-1") ? prev.filter((key) => key !== "header-1") : [...prev, "header-1"]))
                  }
                  items={[
                    {
                      key: "header-1",
                      showArrow: false,
                      classNames: { header: "p-1 rounded", body: "p-0" },
                      label: (
                        <div className="d-flex gap-2 user-select-none ">
                          <div className="d-flex gap-2">
                            <Avatar size="large" src={support?.user?.avatar} />
                            <div>
                              <div className="fw-bold"> {support?.user?.name} </div>
                              <div> {userRoleFormate[support?.user?.role]} </div>
                            </div>
                          </div>
                          <div className="ms-auto small">
                            <div> {dayjs(support?.createdAt).fromNow()} </div>
                          </div>
                        </div>
                      ),
                      children: (
                        <div style={{ marginLeft: "3.50rem", marginRight: "3.5rem" }}>
                          <div dangerouslySetInnerHTML={{ __html: support?.message.replace(/&nbsp;/g, " ") }}></div>

                          <div className="d-flex gap-3 my-3">
                            {Array.isArray(support?.files) &&
                              support?.files?.map((file) => (
                                <div key={file?.uid} style={{ maxWidth: 150, maxHeight: 150, overflow: "hidden" }}>
                                  <Image src={file?.url} alt={file?.name} className="w-100" style={{ maxWidth: "240px" }} />
                                </div>
                              ))}
                          </div>
                        </div>
                      ),
                    },
                  ]}
                  activeKey={headerActiveKey}
                />
              </div>
            </div>
            <hr className="border-secondary-subtle my-2" />

            <div>
              {Array.isArray(supportResponse?.data) &&
                supportResponse?.data?.map((response, i) => (
                  <div key={response?._id}>
                    <Collapse
                      ghost={true}
                      activeKey={bodyActiveKey}
                      onChange={() => setBodyActiveKey((prev) => (prev.includes(i) ? prev.filter((key) => key !== i) : [...prev, i]))}
                      key={i}
                      items={[
                        {
                          key: i,
                          showArrow: false,
                          classNames: { header: "p-1 rounded", body: "p-0" },
                          label: (
                            <div className="d-flex gap-2 user-select-none ">
                              <div className="d-flex gap-2">
                                <Avatar size="large" src={response?.sender?.avatar} />
                                <div>
                                  <div className="fw-bold"> {response?.sender?.name} </div>
                                  <div> {userRoleFormate[response?.sender?.role]} </div>
                                </div>
                              </div>
                              <div className="ms-auto small">
                                <div> {dayjs(response?.createdAt).fromNow()} </div>
                              </div>
                            </div>
                          ),
                          children: (
                            <div style={{ marginLeft: "3.5rem", marginRight: "3.5rem" }}>
                              <div dangerouslySetInnerHTML={{ __html: response?.message.replace(/&nbsp;/g, " ") }}></div>
                            </div>
                          ),
                        },
                      ]}
                      defaultActiveKey={["1"]}
                    />
                    {supportResponse?.data?.length - 1 !== i && <hr className="border-secondary-subtle my-2" />}
                  </div>
                ))}

              {!support?.resolvedBy && supportResponse?.data?.length > 0 ? <hr className="border-secondary-subtle my-2" /> : ""}
            </div>

            {support?.resolvedBy ? (
              ""
            ) : (
              <div className="mt-auto">
                <ResponseForm {...{ support: support }} />
              </div>
            )}
          </>
        ) : (
          <Empty description="No ticket selected" />
        )}
      </div>
    </Spin>
  );
};

export default SupportInboxMessages;
